import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Input } from 'shared/ui/input'
import { Button } from 'shared/ui/button'
import { Label } from 'shared/ui/label'
import { Alert, AlertDescription } from 'shared/ui/alert'
import { HardHat, Lock, ArrowLeft, CheckCircle, AlertTriangle, ShieldCheck } from 'lucide-react'
import { login, type LoginResponseDto } from 'modules/auth/api/authService'
import { isAzureConfigured, startAzureLogin } from 'modules/auth/api/azureAuth'

interface ContractorLoginProps {
  onLogin: (userData: any) => void
  onBack: () => void
}

export function ContractorLogin({ onLogin, onBack }: ContractorLoginProps) {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    contractorId: '',
    password: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isAzureLoading, setIsAzureLoading] = useState(false)
  const [isAzureVisible, setIsAzureVisible] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let isMounted = true

    const loadAzureVisibility = async () => {
      try {
        const configured = await isAzureConfigured('CONTRACTOR')
        if (isMounted) {
          setIsAzureVisible(configured)
        }
      } catch {
        if (isMounted) {
          setIsAzureVisible(false)
        }
      }
    }

    void loadAzureVisibility()

    return () => {
      isMounted = false
    }
  }, [])

  const handleInputChange = (field: 'contractorId' | 'password', value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setError('')
  }

  const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const response: LoginResponseDto = await login({
        username: formData.contractorId.trim(),
        password: formData.password,
        userType: 'CONTRACTOR',
      })
      onLogin({ ...response, userType: 'contractor' })
    } catch (err: any) {
      console.error(err)
      const status = err?.response?.status
      if (status === 401 || status === 403) {
        setError('ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง')
      } else if (status === 423) {
        setError('บัญชีผู้ใช้ถูกระงับ กรุณาติดต่อผู้ดูแลระบบ')
      } else {
        setError('ไม่สามารถเข้าสู่ระบบได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleAzureLogin = async () => {
    setError('')
    if (!(await isAzureConfigured('CONTRACTOR'))) {
      setIsAzureVisible(false)
      setError('ไม่พบการตั้งค่า Azure กรุณาติดต่อผู้ดูแลระบบ')
      return
    }
    setIsAzureLoading(true)
    try {
      await startAzureLogin('CONTRACTOR')
    } catch (err: any) {
      console.error(err)
      const data = err?.response?.data
      const message =
        (typeof data === 'string' ? data : null) ??
        (typeof data?.message === 'string' ? data.message : null) ??
        err?.message ??
        'ไม่สามารถเข้าสู่ระบบด้วย Azure ได้'
      setError(message)
      setIsAzureLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100 flex items-center justify-center p-6">
      <div className="w-full max-w-xl mx-auto flex flex-col items-center gap-4">
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 self-start"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          กลับไปหน้าเลือกประเภท
        </Button>

        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-3">
            <div className="p-3 bg-orange-500 rounded-full shadow-lg">
              <HardHat className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-orange-900">Contractor Portal</h1>
          </div>
        </div>

        <Card className="shadow-xl border-0 bg-white/95 backdrop-blur w-full">
          <CardHeader className="pb-4 text-center space-y-2">
            <CardTitle className="flex items-center justify-center gap-2 text-2xl text-orange-900">
              <Lock className="w-6 h-6 text-orange-500" />
              Contractor Sign In
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {error && (
              <Alert variant="destructive" className="border-red-200 bg-red-50">
                <AlertTriangle className="w-4 h-4" />
                <AlertDescription className="text-red-700">{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="contractorId">Username</Label>
                <Input
                  id="contractorId"
                  type="text"
                  placeholder="Enter Username"
                  value={formData.contractorId}
                  onChange={event => handleInputChange('contractorId', event.target.value)}
                  className="bg-orange-50/60 border-orange-200 focus-visible:ring-orange-400"
                  autoComplete="username"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contractorPassword">Password</Label>
                <Input
                  id="contractorPassword"
                  type="password"
                  placeholder="Enter your Password"
                  value={formData.password}
                  onChange={event => handleInputChange('password', event.target.value)}
                  className="bg-orange-50/60 border-orange-200 focus-visible:ring-orange-400"
                  autoComplete="current-password"
                  required
                />
                <Button
                  type="button"
                  variant="link"
                  className="px-0 text-sm text-orange-600 hover:text-orange-700"
                  onClick={() => navigate("/login/contractor/forgot")}
                >
                  ลืมรหัสผ่าน?
                </Button>
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-lg"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>กำลังตรวจสอบ...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span>เข้าสู่ระบบผู้รับเหมา</span>
                  </>
                )}
              </Button>
            </form>

            {isAzureVisible ? (
              <>
                <div className="flex items-center gap-3 text-xs text-orange-800/70">
                  <div className="h-px flex-1 bg-orange-100" />
                  <span>or sign in with</span>
                  <div className="h-px flex-1 bg-orange-100" />
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-orange-200 text-orange-700 hover:bg-orange-50"
                  onClick={handleAzureLogin}
                  disabled={isAzureLoading}
                >
                  {isAzureLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin mr-2" />
                      Redirecting to Azure...
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="w-4 h-4 mr-2 text-orange-500" />
                      Sign In with Azure AD
                    </>
                  )}
                </Button>
              </>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default ContractorLogin
